// frontend/src/pages/CamDoBrainQLearning.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import { toast } from 'react-toastify';
import './CamDoBrainQLearning.css';

const CONTENT_TYPE_LABELS = { IMAGE: 'Post', CAROUSEL: 'Carrusel', VIDEO: 'Video', REEL: 'Reel' };
const formatType = (type) => CONTENT_TYPE_LABELS[type] || type;

// ─── Component ────────────────────────────────────────────
const CamDoBrainQLearning = () => {
  const [searchParams] = useSearchParams();
  const brandProfileIdParam = searchParams.get('brand_profile_id');

  // Core state
  const [loading, setLoading] = useState(true);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);

  // UI state
  const [training, setTraining] = useState(false);
  const [error, setError] = useState(null);

  // Unified data from /propose/ endpoint
  const [proposalData, setProposalData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  // ─── Load brand profiles ─────────────────────────────
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedBrandId) {
      loadData(selectedBrandId);
    }
  }, [selectedBrandId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const profilesRes = await camdobrainService.getBrandProfiles();
      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data
        : (profilesRes.data?.results || []);
      setBrandProfiles(profiles);

      if (brandProfileIdParam && profiles.find(p => p.id === parseInt(brandProfileIdParam))) {
        setSelectedBrandId(parseInt(brandProfileIdParam));
      } else if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }
    } catch (err) {
      console.error('Error loading profiles:', err);
      setError('Error al cargar perfiles de marca');
    } finally {
      setLoading(false);
    }
  };

  const loadData = useCallback(async (brandId) => {
    try {
      setLoadingData(true);
      const res = await camdobrainService.getQLearningProposal(brandId);
      setProposalData(res.data);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setProposalData(null);
      setError('Error al cargar datos de horarios');
    } finally {
      setLoadingData(false);
    }
  }, []);

  // ─── Actions ──────────────────────────────────────────
  const handleTrain = async () => {
    try {
      setTraining(true);
      await camdobrainService.trainQLearning(selectedBrandId);
      toast.success('Entrenamiento iniciado correctamente');
      setTimeout(() => loadData(selectedBrandId), 2000);
    } catch (err) {
      console.error('Error training:', err);
      toast.error(err.response?.data?.error || 'Error al iniciar entrenamiento');
    } finally {
      setTraining(false);
    }
  };

  // ─── Render ───────────────────────────────────────────
  if (loading) return <Loading />;

  // Extract timing data from the unified response
  const timingData = proposalData?.timing || null;
  const modelVersion = proposalData?.model_version || 0;

  return (
    <div className="ql-page">
      {/* ── Header ────────────────────────────── */}
      <div className="ql-header">
        <div className="ql-header-left">
          <h1>
            Horarios Optimos
          </h1>
        </div>
        <div className="ql-header-actions">
          <select
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(Number(e.target.value))}
            className="ql-brand-select"
          >
            {brandProfiles.map(brand => (
              <option key={brand.id} value={brand.id}>{brand.brand_name}</option>
            ))}
          </select>
          <button
            className="ql-train-btn"
            onClick={handleTrain}
            disabled={training}
          >
            {training ? <span className="ql-spinner" /> : <>⚡ Entrenar Modelo</>}
          </button>
        </div>
      </div>

      {error && (
        <div className="ql-error-banner">
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* ── Proposal Hero ─────────────────────── */}
      <ProposalHero
        data={proposalData}
        loading={loadingData}
        onRefresh={() => loadData(selectedBrandId)}
      />

      {/* ── Timing Content (inline) ───────────── */}
      <TimingContent
        timingData={timingData}
        loading={loadingData}
        modelVersion={modelVersion}
      />
    </div>
  );
};

// ─── Proposal Hero ────────────────────────────────────────
const ProposalHero = ({ data, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="ql-proposal-loading">
        <Loading fullScreen={false} size="sm" mensaje="Calculando mejor momento para publicar" />
      </div>
    );
  }

  if (!data || !data.proposal) return null;

  const { proposal, alternatives, model_trained, fallback } = data;

  const getTimeBadge = (slot) => {
    if (slot.is_today) return <span className="ql-proposal-badge today">Hoy</span>;
    if (slot.is_tomorrow) return <span className="ql-proposal-badge tomorrow">Manana</span>;
    return <span className="ql-proposal-badge later">En {slot.days_from_now} dias</span>;
  };

  return (
    <div className={`ql-proposal-hero${fallback ? ' fallback' : ''}`}>
      <div className="ql-proposal-header">
        <div className="ql-proposal-title">
          <h2>Propuesta de Publicacion</h2>
          <p>{model_trained ? 'El modelo DQN recomienda...' : 'Recomendacion heuristica (entrena el modelo para mayor precision)'}</p>
        </div>
        <button className="ql-proposal-refresh" onClick={onRefresh} disabled={loading}>
          Actualizar
        </button>
      </div>

      <div className="ql-proposal-main">
        <div className="ql-proposal-date">
          <span className="ql-proposal-day-name">{proposal.day_name}</span>
          <span className="ql-proposal-date-display">{proposal.date_display}</span>
          <span className="ql-proposal-hour">{proposal.hour_display}</span>
          {getTimeBadge(proposal)}
        </div>

        <div className="ql-proposal-details">
          <div className="ql-proposal-detail-row">
            <span className="label">Tipo</span>
            <span className="value"><span className="ql-badge-type">{formatType(proposal.content_type)}</span></span>
          </div>
          <div className="ql-proposal-detail-row">
            <span className="label">Trigger</span>
            <span className="value">{proposal.emotional_trigger}</span>
          </div>
          <div className="ql-proposal-detail-row">
            <span className="label">Caption</span>
            <span className="value">{proposal.caption_strategy}</span>
          </div>
          <div className="ql-proposal-detail-row">
            <span className="label">CTA</span>
            <span className="value">{proposal.has_cta ? 'Si' : 'No'}</span>
          </div>
          <div className="ql-proposal-detail-row">
            <span className="label">Hashtags</span>
            <span className="value">{proposal.hashtag_strategy}</span>
          </div>
        </div>

        <div className="ql-proposal-qvalue">
          <span className="label">Q-Value</span>
          {proposal.q_value.toFixed(4)}
        </div>
      </div>

      {alternatives && alternatives.length > 0 && (
        <>
          <p className="ql-proposal-alternatives-title">Alternativas</p>
          <div className="ql-proposal-alternatives">
            {alternatives.map((alt, i) => (
              <div key={i} className="ql-proposal-alt-card">
                <div className="ql-proposal-alt-time">{alt.day_short} {alt.hour_display}</div>
                <div className="ql-proposal-alt-action">
                  {formatType(alt.content_type)} · {alt.emotional_trigger}
                </div>
                <div className="ql-proposal-alt-qvalue">{alt.q_value.toFixed(4)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {fallback && (
        <div className="ql-proposal-fallback-notice">
          Propuesta basada en heuristicas. Entrena el modelo DQN para obtener recomendaciones basadas en tus datos reales.
        </div>
      )}
    </div>
  );
};

// ─── Timing Content (shown directly, no tabs) ────────────
const TimingContent = ({ timingData, loading, modelVersion }) => {
  if (loading) {
    return (
      <div className="ql-section">
        <div className="ql-empty-chart">
          <Loading fullScreen={false} size="sm" mensaje="Calculando heatmap de horarios" />
        </div>
      </div>
    );
  }

  if (modelVersion === 0 || !timingData) {
    return (
      <div className="ql-section">
        <div className="ql-empty-chart">
          <span>⏰</span>
          <p>Entrena el modelo primero para ver el heatmap de horarios optimos.</p>
        </div>
      </div>
    );
  }

  const { heatmap, best_slot, top_3 } = timingData;
  const DAY_SHORT = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
  const HOUR_LABELS = ['Manana', 'Tarde', 'Noche', 'Madrugada'];

  // Calculate min/max for color scaling
  const scores = heatmap.map(h => h.score);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const range = maxScore - minScore || 1;

  const getHeatColor = (score) => {
    const normalized = (score - minScore) / range;
    const r = Math.round(240 - normalized * 200);
    const g = Math.round(240 - normalized * 60);
    const b = Math.round(240 - normalized * 200);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const getTextColor = (score) => {
    const normalized = (score - minScore) / range;
    return normalized > 0.6 ? '#fff' : '#1e293b';
  };

  // Build grid data: rows=days, cols=hour_buckets
  const grid = {};
  heatmap.forEach(item => {
    const key = `${item.day}-${item.hour_bucket}`;
    grid[key] = item;
  });

  // Aggregate scores by day for bar chart
  const dayAggregated = DAY_SHORT.map((name, i) => {
    const daySlots = heatmap.filter(h => h.day === i);
    const avgScore = daySlots.reduce((sum, h) => sum + h.score, 0) / (daySlots.length || 1);
    return { name, score: parseFloat(avgScore.toFixed(4)) };
  });

  return (
    <>
      {/* Best Slot Highlight */}
      {best_slot && (
        <div className="ql-section ql-timing-best-slot">
          <div className="ql-timing-best-header">
            <div>
              <h2>Mejor Horario Recomendado</h2>
              <p className="ql-section-desc">El modelo DQN recomienda este horario como optimo para publicar.</p>
            </div>
          </div>
          <div className="ql-timing-best-body">
            <div className="ql-timing-best-time">
              <span className="ql-timing-best-day">{best_slot.day_name}</span>
              <span className="ql-timing-best-hour">{best_slot.hour_label}</span>
            </div>
            <div className="ql-timing-best-action">
              <span className="ql-timing-best-score">Q-Value: {best_slot.score.toFixed(4)}</span>
              <div className="ql-timing-best-details">
                <span className="ql-badge-type">{formatType(best_slot.best_action.content_type)}</span>
                <span>Caption: {best_slot.best_action.caption_strategy}</span>
                <span>Trigger: {best_slot.best_action.emotional_trigger}</span>
                <span>CTA: {best_slot.best_action.has_cta ? 'Si' : 'No'}</span>
                <span>Hashtags: {best_slot.best_action.hashtag_strategy}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Heatmap Grid */}
      <div className="ql-section">
        <h2>Heatmap de Horarios</h2>
        <p className="ql-section-desc">
          Intensidad del Q-value para cada combinacion dia/hora. Colores mas oscuros = mayor confianza del modelo.
        </p>
        <div className="ql-timing-heatmap-wrapper">
          <div className="ql-timing-heatmap">
            {/* Header row */}
            <div className="ql-timing-heatmap-cell ql-timing-heatmap-corner"></div>
            {HOUR_LABELS.map((label, i) => (
              <div key={i} className="ql-timing-heatmap-cell ql-timing-heatmap-header">{label}</div>
            ))}
            {/* Data rows */}
            {DAY_SHORT.map((dayName, day) => (
              <React.Fragment key={day}>
                <div className="ql-timing-heatmap-cell ql-timing-heatmap-row-label">{dayName}</div>
                {[0, 1, 2, 3].map(bucket => {
                  const item = grid[`${day}-${bucket}`];
                  if (!item) return <div key={bucket} className="ql-timing-heatmap-cell" />;
                  const isBest = best_slot && best_slot.day === day && best_slot.hour_bucket === bucket;
                  return (
                    <div
                      key={bucket}
                      className={`ql-timing-heatmap-cell ql-timing-heatmap-data ${isBest ? 'best' : ''}`}
                      style={{ background: getHeatColor(item.score), color: getTextColor(item.score) }}
                      title={`${item.day_name} ${item.hour_label}: ${item.score.toFixed(4)}`}
                    >
                      <span className="ql-timing-cell-score">{item.score.toFixed(3)}</span>
                      {isBest && <span className="ql-timing-cell-badge">★</span>}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="ql-grid-2">
        {/* Top 3 Slots */}
        <div className="ql-section">
          <h2>Top 3 Horarios</h2>
          <p className="ql-section-desc">Las 3 mejores combinaciones dia/hora segun el modelo.</p>
          <div className="ql-timing-top3">
            {top_3.map((slot, i) => (
              <div key={i} className={`ql-timing-top3-card rank-${i + 1}`}>
                <span className="ql-timing-top3-rank">#{i + 1}</span>
                <div className="ql-timing-top3-info">
                  <span className="ql-timing-top3-time">{slot.day_name} - {slot.hour_label}</span>
                  <span className="ql-timing-top3-action">
                    {formatType(slot.best_action.content_type)} · {slot.best_action.emotional_trigger} · CTA: {slot.best_action.has_cta ? 'Si' : 'No'}
                  </span>
                </div>
                <span className="ql-q-value">{slot.score.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Day of Week Bar Chart */}
        <div className="ql-section">
          <h2>Score por Dia de la Semana</h2>
          <p className="ql-section-desc">Q-value promedio agregado por dia (todas las horas).</p>
          <div className="ql-chart-container">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dayAggregated} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="score" fill="#10b981" name="Q-Value Promedio" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
};

export default CamDoBrainQLearning;
